import {
  APPLEPAYLINK_PAYMENT_METHOD_PLACEHOLDER,
  BIZUM_PAYMENT_METHOD_PLACEHOLDER,
  BLIK_PAYMENT_METHOD_PLACEHOLDER,
  CHEQUE_GOURMET_PAYMENT_METHOD_PLACEHOLDER,
  CHEQUE_GOURMET_VOUCHER_PAYMENT_METHOD_PLACEHOLDER,
  PAYMENT_ON_DELIVERY_CARD_PAYMENT_METHOD_PLACEHOLDER,
  PAYPAL_PAYMENT_METHOD_PLACEHOLDER,
  SODEXO_PAYMENT_METHOD_PLACEHOLDER,
  SODEXO_VOUCHER_PAYMENT_METHOD_PLACEHOLDER,
  TICKET_RESTAURANT_PAYMENT_METHOD_PLACEHOLDER,
  TICKET_RESTAURANT_VOUCHER_PAYMENT_METHOD_PLACEHOLDER,
  WAYLET_PAYMENT_METHOD_PLACEHOLDER,
} from 'state/payment/constants';
import { IPaymentMethod } from 'state/payment/types';
import { isApplePay, isGooglePay } from 'utils/payment/native-payment';

import { IGetAvailablePaymentMethods, ISetupPaymentMethodState } from '../types';

import { getCheckoutPaymentMethodId } from './getCheckoutPaymentMethodId';
import { getPrepaidReloadPaymentMethodId } from './getPrepaidReloadPaymentMethodId';
import { getValidPaymentMethodId } from './getValidPaymentMethodId';
/**
 * Get a valid payment methods list, default, checkout and prepaid payment method ID
 */

const getAvailablePaymentMethods = ({
  accounts,
  applePayCardDetails,
  canUseApplePay,
  canUseGooglePay,
  googlePayCardDetails,
}: IGetAvailablePaymentMethods) => {
  const availablePaymentMethodsWithoutNative = accounts.filter(
    method => !isApplePay(method.fdAccountId) && !isGooglePay(method.fdAccountId)
  );

  // Put Apple/Google Pay on top of the list, after, the prepaid card and finally the CC
  const availablePaymentMethodsWithoutPrepaidOrCash = availablePaymentMethodsWithoutNative.filter(
    method => !method.prepaid && !method.cash
  );

  // Adding Apple Pay if available
  if (canUseApplePay) {
    availablePaymentMethodsWithoutPrepaidOrCash.unshift(applePayCardDetails);
  }
  // Adding Google Pay if available
  if (canUseGooglePay) {
    availablePaymentMethodsWithoutPrepaidOrCash.unshift(googlePayCardDetails);
  }

  return { availablePaymentMethodsWithoutPrepaidOrCash, availablePaymentMethodsWithoutNative };
};

export const getPaymentMethodsState = ({
  paymentMethodId,
  prepaidPaymentMethodId,
  accounts,
  canUseApplePay,
  enableBlik,
  enableCashPayment,
  enablePayPal,
  enableSodexoVoucher,
  enableChequeGourmetVoucher,
  enableTicketRestaurantVoucher,
  enablePayPalPaycomet,
  enableSodexo,
  enableChequeGourmet,
  enableTicketRestaurantCard,
  enableSaveCard,
  applePayCardDetails,
  canUseGooglePay,
  googlePayCardDetails,
  paymentMethodHasBeenInit,
  enablePaymentOnDeliveryCard,
  enableBizum,
  enableWaylet,
  enableApplePayLink,
  // TODO: This parameter/flag is a temporary safeguard, after 1 month in production this should be removed and the final value should always be false.
  enableFirstValidPaymentMethodIdFoundAsDefault = false,
}: ISetupPaymentMethodState) => {
  const { availablePaymentMethodsWithoutPrepaidOrCash, availablePaymentMethodsWithoutNative } =
    getAvailablePaymentMethods({
      accounts,
      applePayCardDetails,
      canUseApplePay,
      canUseGooglePay,
      googlePayCardDetails,
    });

  const isDefaultReloadMethodAGiftCard = (paymentMethod?: IPaymentMethod) =>
    !!paymentMethod?.prepaid;

  const isUsersPaymentMethodGiftCard = isDefaultReloadMethodAGiftCard(
    accounts.find(account => account.accountIdentifier === prepaidPaymentMethodId)
  );

  // Get the default prepaid reload card payment method
  const validDefaultReloadPaymentMethodId = getPrepaidReloadPaymentMethodId({
    paymentMethodHasBeenInit,
    accounts: [...availablePaymentMethodsWithoutNative],
    usersPaymentMethodId: prepaidPaymentMethodId,
    canUseApplePay,
    canUseGooglePay,
    applePayCardDetails,
    googlePayCardDetails,
    isUsersPaymentMethodGiftCard,
  });

  // Get the prepaid reload card payment method
  const validReloadPaymentMethodId = getPrepaidReloadPaymentMethodId({
    paymentMethodHasBeenInit,
    accounts: [...availablePaymentMethodsWithoutNative],
    usersPaymentMethodId: prepaidPaymentMethodId,
    canUseApplePay,
    canUseGooglePay,
    applePayCardDetails,
    googlePayCardDetails,
  });

  const prepaidCard = accounts.find(method => !!method.prepaid);
  const cash = accounts.find(method => !!method.cash);

  const availablePaymentMethodList = availablePaymentMethodsWithoutPrepaidOrCash.filter(method => {
    if (!enableSaveCard && method.credit && Object.keys(method.credit).length) {
      return false;
    }

    return true;
  });

  const availablePaymentMethodListDictionary = availablePaymentMethodList.reduce((acc, method) => {
    if (method.accountIdentifier) {
      acc[method?.accountIdentifier] = true;
    }
    return acc;
  }, {});

  if (prepaidCard) {
    availablePaymentMethodList.unshift(prepaidCard);
  }

  if (enableCashPayment && cash) {
    availablePaymentMethodList.push(cash);
  }

  if (
    enablePayPal &&
    !availablePaymentMethodListDictionary[PAYPAL_PAYMENT_METHOD_PLACEHOLDER.accountIdentifier ?? '']
  ) {
    availablePaymentMethodList.push(PAYPAL_PAYMENT_METHOD_PLACEHOLDER);
  }

  if (
    enableSodexoVoucher &&
    !availablePaymentMethodListDictionary[
      SODEXO_VOUCHER_PAYMENT_METHOD_PLACEHOLDER.accountIdentifier ?? ''
    ]
  ) {
    availablePaymentMethodList.push(SODEXO_VOUCHER_PAYMENT_METHOD_PLACEHOLDER);
  }

  if (
    enableChequeGourmetVoucher &&
    !availablePaymentMethodListDictionary[
      CHEQUE_GOURMET_VOUCHER_PAYMENT_METHOD_PLACEHOLDER.accountIdentifier ?? ''
    ]
  ) {
    availablePaymentMethodList.push(CHEQUE_GOURMET_VOUCHER_PAYMENT_METHOD_PLACEHOLDER);
  }

  if (
    enableTicketRestaurantVoucher &&
    !availablePaymentMethodListDictionary[
      TICKET_RESTAURANT_VOUCHER_PAYMENT_METHOD_PLACEHOLDER.accountIdentifier ?? ''
    ]
  ) {
    availablePaymentMethodList.push(TICKET_RESTAURANT_VOUCHER_PAYMENT_METHOD_PLACEHOLDER);
  }

  if (
    enableBlik &&
    !availablePaymentMethodListDictionary[BLIK_PAYMENT_METHOD_PLACEHOLDER.accountIdentifier ?? '']
  ) {
    availablePaymentMethodList.push(BLIK_PAYMENT_METHOD_PLACEHOLDER);
  }

  if (
    enablePayPalPaycomet &&
    !availablePaymentMethodListDictionary[PAYPAL_PAYMENT_METHOD_PLACEHOLDER.accountIdentifier ?? '']
  ) {
    availablePaymentMethodList.push(PAYPAL_PAYMENT_METHOD_PLACEHOLDER);
  }

  if (
    enableSodexo &&
    !availablePaymentMethodListDictionary[SODEXO_PAYMENT_METHOD_PLACEHOLDER.accountIdentifier ?? '']
  ) {
    availablePaymentMethodList.push(SODEXO_PAYMENT_METHOD_PLACEHOLDER);
  }

  if (
    enableChequeGourmet &&
    !availablePaymentMethodListDictionary[
      CHEQUE_GOURMET_PAYMENT_METHOD_PLACEHOLDER.accountIdentifier ?? ''
    ]
  ) {
    availablePaymentMethodList.push(CHEQUE_GOURMET_PAYMENT_METHOD_PLACEHOLDER);
  }

  if (
    enableTicketRestaurantCard &&
    !availablePaymentMethodListDictionary[
      TICKET_RESTAURANT_PAYMENT_METHOD_PLACEHOLDER.accountIdentifier ?? ''
    ]
  ) {
    availablePaymentMethodList.push(TICKET_RESTAURANT_PAYMENT_METHOD_PLACEHOLDER);
  }

  if (
    enablePaymentOnDeliveryCard &&
    !availablePaymentMethodListDictionary[
      PAYMENT_ON_DELIVERY_CARD_PAYMENT_METHOD_PLACEHOLDER.accountIdentifier ?? ''
    ]
  ) {
    availablePaymentMethodList.push(PAYMENT_ON_DELIVERY_CARD_PAYMENT_METHOD_PLACEHOLDER);
  }

  if (
    enableBizum &&
    !availablePaymentMethodListDictionary[BIZUM_PAYMENT_METHOD_PLACEHOLDER.accountIdentifier ?? '']
  ) {
    availablePaymentMethodList.push(BIZUM_PAYMENT_METHOD_PLACEHOLDER);
  }

  if (
    enableWaylet &&
    !availablePaymentMethodListDictionary[WAYLET_PAYMENT_METHOD_PLACEHOLDER.accountIdentifier ?? '']
  ) {
    availablePaymentMethodList.push(WAYLET_PAYMENT_METHOD_PLACEHOLDER);
  }

  if (
    enableApplePayLink &&
    !availablePaymentMethodListDictionary[
      APPLEPAYLINK_PAYMENT_METHOD_PLACEHOLDER.accountIdentifier ?? ''
    ]
  ) {
    availablePaymentMethodList.push(APPLEPAYLINK_PAYMENT_METHOD_PLACEHOLDER);
  }

  // Get the default payment method
  const validDefaultPaymentMethodId = getValidPaymentMethodId({
    canUseApplePay,
    canUseGooglePay,
    paymentMethodId,
    paymentMethods: availablePaymentMethodList,
    // TODO: This parameter/flag is a temporary safeguard, after 1 month in production this should be removed and the final value should always be false.
    returnFirstValid: enableFirstValidPaymentMethodIdFoundAsDefault,
  });

  // Get the checkout payment method
  const validCheckoutPaymentMethodId = getCheckoutPaymentMethodId({
    paymentMethodHasBeenInit,
    accounts: [...availablePaymentMethodsWithoutNative],
    usersPaymentMethodId: paymentMethodId,
    canUseApplePay,
    applePayCardDetails,
    canUseGooglePay,
    googlePayCardDetails,
  });

  return {
    availablePaymentMethodList,
    validDefaultPaymentMethodId,
    validCheckoutPaymentMethodId,
    validReloadPaymentMethodId,
    validDefaultReloadPaymentMethodId,
  };
};
